import { writable } from 'svelte/store';
import { browser } from '$app/environment';

export type Theme = 'light' | 'dark' | 'system';

// Get initial theme synchronously to prevent flash
function getInitialTheme(): Theme {
	if (!browser) return 'system';
	const stored = localStorage.getItem('theme') as Theme;
	return stored || 'system';
}

function createThemeStore() {
	// Initialize with actual theme from localStorage
	const initialTheme = getInitialTheme();
	const { subscribe, set } = writable<Theme>(initialTheme);
	
	function applyTheme(theme: Theme) {
		if (!browser) return;
		
		const root = document.documentElement;
		
		// Add transition class
		root.classList.add('theme-transition');
		
		if (theme === 'system') {
			const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
			root.setAttribute('data-theme', prefersDark ? 'dark' : 'light');
		} else {
			root.setAttribute('data-theme', theme);
		}
		
		// Remove transition class after animation
		setTimeout(() => {
			root.classList.remove('theme-transition');
		}, 300);
	}
	
	return {
		subscribe,
		set: (theme: Theme) => {
			set(theme);
			if (browser) {
				localStorage.setItem('theme', theme);
				applyTheme(theme);
			}
		},
		initialize: () => {
			if (browser) {
				// Don't re-apply theme if it's already set correctly by inline script
				const currentDataTheme = document.documentElement.getAttribute('data-theme');
				const stored = localStorage.getItem('theme') as Theme || 'system';
				
				// Only update if necessary
				if (!currentDataTheme) {
					applyTheme(stored);
				}
				
				// Update store to match
				set(stored);
				
				// Listen for system theme changes
				const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
				mediaQuery.addEventListener('change', () => {
					const currentTheme = localStorage.getItem('theme') as Theme;
					if (currentTheme === 'system') {
						applyTheme('system');
					}
				});
			}
		}
	};
}

export const theme = createThemeStore();